import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {

  private leaveTypeSubject = new BehaviorSubject<any>(null);
  leaveType = this.leaveTypeSubject.asObservable();

  private projectSubject = new BehaviorSubject<any>(null);
  project = this.projectSubject.asObservable();

  private customerSubject = new BehaviorSubject<any>(null);
  customer = this.customerSubject.asObservable();

  private accountSubject = new BehaviorSubject<any>(null);
  account = this.accountSubject.asObservable();

  private viewOnlyDialogSubject = new BehaviorSubject<boolean>(false);
  viewOnlyDialog = this.viewOnlyDialogSubject.asObservable();

  setViewOnlyDialog(value: boolean) {
    this.viewOnlyDialogSubject.next(value);
  }

  truncateWordLenth(description: string, maxLength: number): string {
    if (description.length <= maxLength) {
      return description;
    } else {
      return description.substr(0, maxLength) + '...';
    }
  }
  setLeaveType(data: any) {
    this.leaveTypeSubject.next(data);
  }

  getLeaveType() {
    return this.leaveTypeSubject.getValue();
  }

  setProjectdetails(data: any) {
    this.projectSubject.next(data);
  }

  getProjectdetails() {
    return this.projectSubject.getValue();
  }

  setCustomerDetails(data: any) {
    this.customerSubject.next(data);
    localStorage.setItem('customerDetails', JSON.stringify(data));
  }

  // getCustomerdetails() {
  //   return this.customerSubject.getValue();
  // }

  getCustomerdetails(): any {
    const current = this.customerSubject.getValue();
    if (current) {
      return current;
    } else {
      const stored = localStorage.getItem('customerDetails');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.customerSubject.next(parsed);
        return parsed;
      }
      return null;
    }
  }

  setAccountDetails(data: any) {
    this.accountSubject.next(data);
    localStorage.setItem('accountDetails', JSON.stringify(data));
  }

  // getAccountdetails() {
  //   return this.accountSubject.getValue();
  // }

  getAccountdetails(): any {
    const current = this.accountSubject.getValue();
    if (current) {
      return current;
    } else {
      const stored = localStorage.getItem('accountDetails');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.accountSubject.next(parsed);
        return parsed;
      }
      return null;
    }
  }

  clearAccountDetails() {
    this.accountSubject.next(null);
    localStorage.removeItem('accountDetails');
  }

  clearCustomerDetails() {
    this.customerSubject.next(null);
    localStorage.removeItem('customerDetails');
  }

  getFormattedLocalDate(date: Date | moment.Moment | null | undefined): string {
    // `date` can be a plain Date (most callers) or a Moment instance — Angular Material's
    // mat-date-range-input hands back a Moment when the module's DateAdapter is MomentDateAdapter
    // (e.g. Timesheet's date-range filter). Native Date getters don't exist on Moment objects,
    // so route everything through moment() instead of calling .getFullYear()/.getMonth()/.getDate() directly.
    if (!date) {
      return '';
    }
    return moment(date).format('YYYY-MM-DD');
  }

  paresedDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  formatDateInput(event: Event) {
    let value = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
    if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length > 5) {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
    (event.target as HTMLInputElement).value = value.substring(0, 10);
  }

  preventNegative(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  blockNegativePaste(event: ClipboardEvent) {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (/[-+eE]/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  allowOnlyThreeDigitPositive(event: KeyboardEvent) {
    const inputChar = event.key;
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }
    const inputElement = event.target as HTMLInputElement;
    const currentValue = inputElement.value + inputChar;
    if (/^0\d*/.test(currentValue)) {
      event.preventDefault();
      return;
    }
    if (currentValue.length > 3) {
      event.preventDefault();
    }
  }
  lockInvalidPaste(event: ClipboardEvent) {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    // Allow only 1–999 (no negative, no decimals, no 0 start)
    if (!/^(?:[1-9][0-9]{0,2})$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  validatePolicyVersionInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.toUpperCase();

    const validPattern = /^(N|NA|[0-9!@#$%^&*()._-]+)$/;

    if (!validPattern.test(value)) {
      value = value
        .replace(/[^0-9!@#$%^&*()._N-]/gi, '')
        .replace(/(?!^N$)N+/g, '');
    }

    input.value = value;
  }

  private isAdminSubject = new BehaviorSubject<boolean>(false);
  isAdmin$ = this.isAdminSubject.asObservable();

  setIsAdmin(roleList: any[]) {
    const isAdmin = roleList?.some((r: any) => r.roleCode === 'ADMIN');
    this.isAdminSubject.next(isAdmin);
  }

  getIsAdmin(): boolean {
    return this.isAdminSubject.getValue();
  }

  private empIdSubject = new BehaviorSubject<number>(Number(localStorage.getItem('empId') || 0));
  empId$ = this.empIdSubject.asObservable();

  setEmpId(id: number) {
    this.empIdSubject.next(id);
  }

  getEmpId(): number {
    return this.empIdSubject.getValue();
  }

  // Starts null so sidebar always waits for header.getaccount() to provide the fresh id,
  // preventing stale proxy-session ids from being used after proxy logout.
  private userIdSubject = new BehaviorSubject<string | null>(null);
  userId$ = this.userIdSubject.asObservable();

  setUserId(id: string) {
    this.userIdSubject.next(id);
  }

  // Called on logout. This service outlives the session (logout navigates without a reload),
  // so without this the next login in the same tab replays the previous user's id/role state.
  resetUserSession() {
    this.userIdSubject.next(null);
    this.empIdSubject.next(0);
    this.isAdminSubject.next(false);
  }

  formatDateIgnoringTimezone(dateValue: any): string {
    if (!dateValue) return '';

    let dateStr = '';

    if (typeof dateValue === 'string') {
      dateStr = dateValue;
    } else if (dateValue instanceof Date) {
      dateStr = dateValue.toISOString().split('T')[0];
    }

    if (!dateStr) return '';

    const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${day}-${month}-${year}`;
    }

    return dateStr;
  }
}